// Trio fixo: nome (até 30 caracteres, com o filtro de palavras do lote 2), escudo de um conjunto pronto e até 3
// membros. Quem cria é o capitão; se ele sair, a capitania passa para quem entrou primeiro (o capitão é sempre o
// membro mais antigo). O limite de trios do plano conta todos os trios de que o jogador participa; ao descer de
// plano, os trios além do limite ficam guardados (os mais novos) e não entram na busca de mesa.
import { ofensivo } from '@truco6/moderacao';
import { MATRIZ, type Plano, type TriosFixos } from '@truco6/planos';

export const MEMBROS_DO_TRIO = 3;
export const NOME_DE_TRIO_MAX = 30;

// Escudos prontos: emoji e cor do design system (tokens de @truco6/ui), sem upload.
export const ESCUDOS = [
  { id: 'zap', emoji: '♣️', cor: 'felt500' },
  { id: 'fogo', emoji: '🔥', cor: 'fire500' },
  { id: 'coroa', emoji: '👑', cor: 'gold400' },
  { id: 'raio', emoji: '⚡', cor: 'nos400' },
  { id: 'galo', emoji: '🐓', cor: 'eles400' },
  { id: 'estrela', emoji: '⭐', cor: 'wood500' },
  { id: 'cartola', emoji: '🎩', cor: 'prata600' },
  { id: 'leao', emoji: '🦁', cor: 'bronze400' },
] as const;
export type IdEscudo = (typeof ESCUDOS)[number]['id'];
export const ehEscudo = (x: unknown): x is IdEscudo => ESCUDOS.some((e) => e.id === x);
export const escudoDe = (id: string) => ESCUDOS.find((e) => e.id === id) ?? ESCUDOS[0];

export type ResultadoNomeTrio = 'ok' | 'vazio' | 'longo' | 'ofensivo';
export function validarNomeDeTrio(nome: string): ResultadoNomeTrio {
  const n = nome.trim();
  if (!n) return 'vazio';
  if ([...n].length > NOME_DE_TRIO_MAX) return 'longo';
  return ofensivo(n) ? 'ofensivo' : 'ok';
}
export const MENSAGEM_NOME_TRIO: Record<Exclude<ResultadoNomeTrio, 'ok'>, string> = {
  vazio: 'Dê um nome ao trio.',
  longo: `O nome do trio tem até ${NOME_DE_TRIO_MAX} caracteres.`,
  ofensivo: 'Escolha outro nome para o trio.',
};

export const triosDoPlano = (plano: Plano): TriosFixos => MATRIZ.triosFixos.valores[plano];

export interface MembroTrio { usuario: string; apelido: string; avatar: string | null; entrouEm: number; capitao: boolean }
export interface Trio { id: string; nome: string; escudo: string; convite: string; criadoEm: number; membros: MembroTrio[] }
export interface MeuTrio extends Trio { ativo: boolean } // dentro do limite do plano: entra na busca de mesa

export interface PartidaDoTrio { partida: string; em: number; venceu: boolean; placar: [number, number]; pontos: number }
export interface HistoricoTrio {
  partidas: number;
  vitorias: number;
  aproveitamento: number; // vitórias / partidas, de 0 a 100
  pontosTemporada: number;
  posicao: number | null; // no ranking de trios do Brasil, na temporada
  ultimas: PartidaDoTrio[]; // as últimas 10
}

export interface PainelTrios extends TriosFixos { trios: MeuTrio[] }

export type ErroTrio = 'sem_plano' | 'limite' | 'cheio' | 'ja_esta' | 'inexistente' | 'nome' | 'escudo';

// Quem cria ou entra: o apelido e o avatar só valem no armazém em memória (na produção, vêm de perfis).
export interface Quem { usuario: string; apelido: string; avatar: string | null }

export class ErroDoTrio extends Error {
  readonly codigo: ErroTrio;
  readonly limite: number | null;
  constructor(codigo: ErroTrio, mensagem: string, limite: number | null = null) {
    super(mensagem);
    this.codigo = codigo;
    this.limite = limite;
  }
}
export const MENSAGEM_TRIO: Record<Exclude<ErroTrio, 'nome'>, string> = {
  sem_plano: 'Criar trio fixo é dos planos Bronze, Prata e Ouro. No Grátis, você entra no trio de um amigo.',
  limite: 'Seu plano permite {n} {trios}.',
  cheio: 'Este trio já tem 3 jogadores.',
  ja_esta: 'Você já está neste trio.',
  inexistente: 'Este convite de trio não existe mais.',
  escudo: 'Escolha um escudo da lista.',
};
export const mensagemDoLimite = (n: number) => MENSAGEM_TRIO.limite.replace('{n}', String(n)).replace('{trios}', n === 1 ? 'trio' : 'trios');

// Os trios ativos de um jogador: os mais antigos (pela entrada dele), até o limite do plano.
export function ativos<T extends { id: string; entrei: number }>(meus: T[], limite: number): Set<string> {
  return new Set([...meus].sort((a, b) => a.entrei - b.entrei).slice(0, limite).map((t) => t.id));
}

// Capitão: quem entrou primeiro.
export function comCapitao(membros: Omit<MembroTrio, 'capitao'>[]): MembroTrio[] {
  const ms = [...membros].sort((a, b) => a.entrouEm - b.entrouEm);
  return ms.map((m, i) => ({ ...m, capitao: i === 0 }));
}

const LETRAS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function novoConvite(sorte: () => number = Math.random): string {
  let c = '';
  for (let i = 0; i < 6; i++) c += LETRAS[Math.floor(sorte() * LETRAS.length)];
  return c;
}
